/**
 * Question Type Definitions
 * Central registry mapping question types to their requirements and validation rules
 *
 * RENAMED from questionTypeRegistry.ts → questionTypeDefinitions.ts (Phase K-1)
 * to disambiguate from src/config/questionTypeRegistry.ts (runtime renderer mapping).
 *
 * This is the single source of truth for:
 * - What fields each question type requires
 * - Which renderer handles each type
 * - What visual aids are appropriate
 * - Type-specific validation rules
 */

import { QuestionType } from '../content'
import type { VisualAidType } from '../visuals'

export interface QuestionTypeDefinition {
  type: QuestionType
  category: 'standard' | 'interactive' | 'visual-data' | 'specialized'
  renderer: string
  description: string
  requiredFields: string[]
  optionalFields: string[]
  dataStructure?: {
    required: string[]
    optional: string[]
  }
  visualRequirements?: {
    stimulus?: VisualAidType[]
    support?: VisualAidType[]
  }
  validationRules: string[]
  examples: string[]
}

/**
 * Complete Question Type Registry
 */
export const QUESTION_TYPE_REGISTRY: Record<
  QuestionType,
  QuestionTypeDefinition
> = {
  // ─── STANDARD TYPES ─────────────────────────────────────────────────────────

  multiple_choice: {
    type: 'multiple_choice',
    category: 'standard',
    renderer: 'src/components/practice/renderers/MultipleChoiceRenderer.tsx',
    description: 'Standard multiple choice with 2-5 options',
    requiredFields: ['question', 'options', 'correctAnswer'],
    optionalFields: ['context', 'stimulusAids', 'supportAids'],
    validationRules: [
      'options.length >= 2 && options.length <= 5',
      'correctAnswer >= 0 && correctAnswer < options.length',
      'All options must be non-empty strings',
      'No duplicate options',
    ],
    examples: ['GET-BREUK-BASIS', 'TIJD-ANALOOG', 'SWW-TT-STAM'],
  },

  // ─── INTERACTIVE TYPES ──────────────────────────────────────────────────────

  verb_conjugation_flow: {
    type: 'verb_conjugation_flow',
    category: 'interactive',
    renderer:
      'src/components/practice/renderers/VerbConjugationFlowRenderer.tsx',
    description:
      'Interactive decision-tree for verb conjugation (e.g. kofschip, hebben/zijn)',
    requiredFields: ['question'],
    optionalFields: ['context', 'stimulusAids', 'supportAids'],
    dataStructure: {
      required: ['verb', 'targetForm', 'steps', 'finalAnswer'],
      optional: ['context'],
    },
    validationRules: [
      'steps must be an array of at least 2 steps',
      'choice steps must have options with exactly one isCorrect: true',
      'input steps must have acceptedAnswers array',
    ],
    examples: ['SWW-VD-HULPWW', 'SWW-VT-VZFS'],
  },

  fill_in: {
    type: 'fill_in',
    category: 'interactive',
    renderer: 'src/components/practice/renderers/FillInRenderer.tsx',
    description: 'Text input field for short answers',
    requiredFields: ['question', 'correctAnswer'],
    optionalFields: [
      'context',
      'acceptableAnswers',
      'caseSensitive',
      'stimulusAids',
      'supportAids',
    ],
    dataStructure: {
      required: [],
      optional: [
        'acceptedAnswers',
        'caseSensitive',
        'placeholder',
        'inputType',
      ],
    },
    validationRules: [
      'correctAnswer must be a string or number',
      'If data.acceptedAnswers provided, must be array of strings',
      'If data.caseSensitive provided, must be boolean',
    ],
    examples: [
      'SWW-TT-STAM (fill-in variant)',
      'SPR-WEET-EI-IJ',
      'GET-KOMMA-AFRONDEN (fill-in variant)',
    ],
  },

  drag_drop_fill: {
    type: 'drag_drop_fill',
    category: 'interactive',
    renderer: 'src/components/practice/renderers/DragDropFillRenderer.tsx',
    description: 'Drag words/phrases into blanks in text',
    requiredFields: ['question', 'data'],
    optionalFields: ['context'],
    dataStructure: {
      required: ['template', 'draggables', 'correctPlacements'],
      optional: ['distractors'],
    },
    validationRules: [
      'data.template must contain {0}, {1}, etc. placeholders',
      'data.draggables must be array of strings',
      'data.correctPlacements must map placeholder indices to draggable indices',
    ],
    examples: ['TAA-ONT-BASIS', 'SPR-KLANK-JAGER'],
  },

  matching: {
    type: 'matching',
    category: 'interactive',
    renderer: 'src/components/practice/renderers/MatchingRenderer.tsx',
    description: 'Match items from left column to right column',
    requiredFields: ['question', 'data'],
    optionalFields: ['context'],
    dataStructure: {
      required: [],
      optional: [
        'leftItems',
        'rightItems',
        'correctMatches',
        'distractors',
        'pairs',
      ],
    },
    validationRules: [
      'Supports two data formats:',
      'Format A (pairs): data.pairs = [{left, right}, ...] — at least 2 pairs',
      'Format B (canonical): data.leftItems + data.rightItems + data.correctMatches',
      'Also accepts nested: data.matching.pairs = [{left, right}, ...]',
      'All indices must be valid',
    ],
    examples: ['TAA-SOORT-WW', 'GET-VERH-BREUK-PROC-KOPPEL', 'TOPO-NL-PROV'],
  },

  ordering: {
    type: 'ordering',
    category: 'interactive',
    renderer: 'src/components/practice/renderers/OrderingRenderer.tsx',
    description: 'Arrange items in correct order',
    requiredFields: ['question', 'data'],
    optionalFields: ['context'],
    dataStructure: {
      required: ['items', 'correctOrder'],
      optional: [],
    },
    validationRules: [
      'data.items must be array of strings',
      'data.correctOrder must be array of indices',
      'correctOrder must be a permutation of [0, 1, 2, ...]',
    ],
    examples: ['STU-ALFABET', 'MET-TIJD-NAUWKEURIG'],
  },

  table_reading: {
    type: 'table_reading',
    category: 'interactive',
    renderer: 'src/components/practice/renderers/TableReadingRenderer.tsx',
    description: 'Read and interpret data from tables',
    requiredFields: ['question', 'data', 'options', 'correctAnswer'],
    optionalFields: ['context'],
    dataStructure: {
      required: ['headers', 'rows'],
      optional: ['caption', 'highlightCell'],
    },
    validationRules: [
      'data.headers must be array of strings',
      'data.rows must be array of arrays',
      'All rows must have same length as headers',
    ],
    examples: ['STU-TABEL', 'TIJD-SCHEMA'],
  },

  number_sequence: {
    type: 'number_sequence',
    category: 'interactive',
    renderer: 'src/components/practice/renderers/NumberSequenceRenderer.tsx',
    description: 'Complete number sequences or patterns',
    requiredFields: ['question', 'data'],
    optionalFields: ['context'],
    dataStructure: {
      required: ['sequence', 'missingIndex', 'correctValue'],
      optional: ['rule'],
    },
    validationRules: [
      'data.sequence must be array of numbers or null',
      'data.missingIndex must be valid index',
      'data.correctValue must be a number',
    ],
    examples: ['GET-GROOT-POS', 'GET-KOMMA-GETALLENLIJN'],
  },

  ratio_table: {
    type: 'ratio_table',
    category: 'interactive',
    renderer: 'src/components/practice/renderers/RatioTableRenderer.tsx',
    description: 'Complete ratio tables with proportional reasoning',
    requiredFields: ['question', 'data'],
    optionalFields: ['context'],
    dataStructure: {
      required: ['headers', 'rows', 'hiddenCells'],
      optional: [],
    },
    validationRules: [
      'data.headers must be array of 2 strings',
      'data.rows must be array of [number, number] pairs',
      'data.hiddenCells must be array of {row, col} objects',
      'Each hiddenCell must reference a valid cell in the rows array',
      'Ratio must be consistent across rows',
    ],
    visualRequirements: {
      stimulus: ['ratio'],
      support: ['ratio'],
    },
    examples: ['GET-VERH-RECEPT', 'GET-VERH-SCHAAL'],
  },

  text_highlight: {
    type: 'text_highlight',
    category: 'interactive',
    renderer: 'src/components/practice/renderers/TextHighlightRenderer.tsx',
    description: 'Highlight specific words or phrases in text',
    requiredFields: ['question', 'data'],
    optionalFields: ['context'],
    dataStructure: {
      required: [],
      optional: ['text', 'correctHighlights', 'maxHighlights', 'segments'],
    },
    validationRules: [
      'Supports two data formats:',
      'Format A (position-based): data.text + data.correctHighlights = [{start, end}, ...]',
      'Format B (segments): data.segments = [{id, text, isCorrect, isTappable}, ...]',
      'At least one correct highlight/segment must exist',
    ],
    examples: ['TAA-LEZEN-DETAIL', 'TAA-SIGNAALWOORD', 'SWW-TT-INV-JIJ'],
  },

  // ─── VISUAL DATA TYPES ──────────────────────────────────────────────────────

  bar_chart: {
    type: 'bar_chart',
    category: 'visual-data',
    renderer: 'src/components/practice/renderers/BarChartRenderer.tsx',
    description: 'Read and interpret bar charts',
    requiredFields: ['question', 'data', 'options', 'correctAnswer'],
    optionalFields: ['context'],
    dataStructure: {
      required: [],
      optional: [
        'data',
        'labels',
        'values',
        'title',
        'xAxisLabel',
        'yAxisLabel',
        'correctIndex',
        'hideValues',
        'barColor',
      ],
    },
    validationRules: [
      'Supports two data formats:',
      'Format A (canonical): data.data = [{label, value}, ...] — at least 2 entries',
      'Format B (legacy): data.labels + data.values arrays — same length',
      'Each entry must have a string label and numeric value',
    ],
    examples: ['STU-GRAFIEK', 'STU-STAAF'],
  },

  line_chart: {
    type: 'line_chart',
    category: 'visual-data',
    renderer: 'src/components/practice/renderers/LineChartRenderer.tsx',
    description: 'Read and interpret line charts',
    requiredFields: ['question', 'data', 'options', 'correctAnswer'],
    optionalFields: ['context'],
    dataStructure: {
      required: [],
      optional: [
        'data',
        'labels',
        'values',
        'title',
        'xAxisLabel',
        'yAxisLabel',
        'correctIndex',
        'hideValues',
      ],
    },
    validationRules: [
      'Supports two data formats:',
      'Format A (canonical): data.data = [{label, value}, ...] — at least 2 entries',
      'Format B (legacy): data.labels + data.values arrays — same length',
      'Each entry must have a string label and numeric value',
    ],
    examples: ['STU-LIJN', 'TIJD-DIAGRAM'],
  },

  schedule_reading: {
    type: 'schedule_reading',
    category: 'visual-data',
    renderer: 'src/components/practice/renderers/ScheduleRenderer.tsx',
    description: 'Read and interpret schedules/timetables',
    requiredFields: ['question', 'data', 'options', 'correctAnswer'],
    optionalFields: ['context'],
    dataStructure: {
      required: ['headers', 'rows'],
      optional: ['title', 'highlightCell'],
    },
    validationRules: [
      'data.headers must be array of strings',
      'data.rows must be array of arrays',
    ],
    examples: ['TIJD-SCHEMA', 'TIJD-PLANNING'],
  },

  pie_chart: {
    type: 'pie_chart',
    category: 'visual-data',
    renderer: 'src/components/practice/renderers/PieChartRenderer.tsx',
    description: 'Read and interpret pie/donut charts by clicking segments',
    requiredFields: ['question', 'data', 'options', 'correctAnswer'],
    optionalFields: ['context'],
    dataStructure: {
      required: [],
      optional: [
        'labels',
        'values',
        'correctIndex',
        'title',
        'colors',
        'hideValues',
      ],
    },
    validationRules: [
      'data.labels and data.values must have same length',
      'data.values must be array of numbers',
      'data.correctIndex must be valid index into labels/values',
      'data.labels must be array of non-empty strings',
    ],
    examples: ['STU-CIRKEL'],
  },

  // ─── SPECIALIZED TYPES ──────────────────────────────────────────────────────

  cijferend_rekenen: {
    type: 'cijferend_rekenen',
    category: 'specialized',
    renderer: 'src/components/practice/renderers/ColumnArithmeticRenderer.tsx',
    description:
      'Column arithmetic (add/subtract/multiply) with step-by-step visualization',
    requiredFields: ['question', 'data'],
    optionalFields: ['context'],
    dataStructure: {
      required: ['operand1', 'operand2', 'operation'],
      optional: ['acceptedAnswers', 'showSteps', 'currentStep'],
    },
    validationRules: [
      'data.operand1 and data.operand2 must be positive integers',
      'data.operation must be one of: +, -, ×',
      'If operation is -, data.operand1 must be >= data.operand2',
    ],
    visualRequirements: {
      stimulus: ['column'],
      support: ['column'],
    },
    examples: ['REK-CIJF-PLUS', 'REK-CIJF-MIN', 'REK-CIJF-KEER'],
  },

  /**
   * Defense-in-depth: cijferend_aftrekken/vermenigvuldigen are NOT in the
   * QuestionType union (content uses 'cijferend_rekenen' for +/−/×).
   * These definitions exist so typeStage validation still works if the
   * union is ever expanded, and to document the expected data shape.
   */
  cijferend_aftrekken: {
    type: 'cijferend_aftrekken',
    category: 'specialized',
    renderer: 'src/components/practice/renderers/ColumnArithmeticRenderer.tsx',
    description: 'Column subtraction with step-by-step visualization',
    requiredFields: ['question', 'data'],
    optionalFields: ['context'],
    dataStructure: {
      required: ['operand1', 'operand2'],
      optional: ['showSteps', 'currentStep'],
    },
    validationRules: [
      'data.operand1 must be >= data.operand2',
      'Both operands must be positive integers',
    ],
    visualRequirements: {
      stimulus: ['column'],
      support: ['column'],
    },
    examples: ['REK-CIJF-MIN'],
  },

  cijferend_vermenigvuldigen: {
    type: 'cijferend_vermenigvuldigen',
    category: 'specialized',
    renderer: 'src/components/practice/renderers/ColumnArithmeticRenderer.tsx',
    description: 'Column multiplication with step-by-step visualization',
    requiredFields: ['question', 'data'],
    optionalFields: ['context'],
    dataStructure: {
      required: ['operand1', 'operand2'],
      optional: ['acceptedAnswers', 'showSteps', 'currentStep'],
    },
    validationRules: [
      'data.operand1 and data.operand2 must be positive integers',
    ],
    visualRequirements: {
      stimulus: ['column'],
      support: ['column'],
    },
    examples: ['REK-CIJF-KEER'],
  },

  cijferend_delen: {
    type: 'cijferend_delen',
    category: 'specialized',
    renderer: 'src/components/practice/renderers/LongDivisionRenderer.tsx',
    description: 'Long division with step-by-step visualization',
    requiredFields: ['question', 'data'],
    optionalFields: ['context'],
    dataStructure: {
      required: ['operand1', 'operand2'],
      optional: ['acceptedAnswers', 'showSteps', 'currentStep'],
    },
    validationRules: [
      'data.operand1 and data.operand2 must be positive integers',
      'data.operand2 must not be zero',
    ],
    visualRequirements: {
      stimulus: [],
      support: ['column'],
    },
    examples: ['REK-CIJF-DEEL'],
  },

  clock: {
    type: 'clock',
    category: 'specialized',
    renderer: 'src/components/practice/renderers/ClockRenderer.tsx',
    description: 'Clock reading and time interpretation (MeesterJay 4.1)',
    requiredFields: ['question', 'data'],
    optionalFields: ['context', 'options', 'correctAnswer'],
    dataStructure: {
      required: ['mode', 'target'],
      optional: ['analog', 'digital', 'options'],
    },
    validationRules: [
      'data.mode must be one of: read_analog_fill, read_digital_fill, analog_to_digital_mc, digital_to_analog_mc',
      'data.target must have hours (0-23) and minutes (0-59)',
      'If mode ends with _mc, options array is required',
    ],
    visualRequirements: {
      stimulus: ['clock', 'digitalClock'],
      support: ['clock', 'digitalClock', 'rule'],
    },
    examples: ['TIJD-ANALOOG', 'TIJD-DIGITAAL', 'TIJD-CONVERSIE'],
  },

  money: {
    type: 'money',
    category: 'specialized',
    renderer: 'src/components/practice/renderers/MoneyAssemblerRenderer.tsx',
    description: 'Money assembly and change calculation (MeesterJay 4.1)',
    requiredFields: ['question', 'data'],
    optionalFields: ['context', 'options', 'correctAnswer'],
    dataStructure: {
      required: ['mode', 'targetCents', 'allowedDenoms'],
      optional: ['priceCents', 'paidCents', 'constraints', 'options'],
    },
    validationRules: [
      'data.mode must be: make_amount or give_change',
      'data.targetCents must be positive integer',
      'data.allowedDenoms must be array of {valueCents, type, label}',
      'If mode is give_change, priceCents and paidCents are required',
    ],
    visualRequirements: {
      stimulus: ['money'],
      support: ['money', 'rule'],
    },
    examples: ['GELD-MAAK', 'GELD-TERUG', 'GELD-WISSEL'],
  },

  multiplication_grid: {
    type: 'multiplication_grid',
    category: 'specialized',
    renderer:
      'src/components/practice/renderers/MultiplicationGridRenderer.tsx',
    description:
      'Interactive multiplication grid/table with hidden cells to fill in',
    requiredFields: ['question', 'data'],
    optionalFields: ['context', 'stimulusAids', 'supportAids'],
    dataStructure: {
      required: ['mode'],
      optional: ['baseNumber', 'range', 'hiddenCells', 'rows', 'cols'],
    },
    validationRules: [
      'data.mode must be one of: single_table, cross_table',
      'If mode is single_table: data.baseNumber and data.range required, data.hiddenCells = indices into range',
      'If mode is cross_table: data.rows and data.cols required, data.hiddenCells = [{row, col}]',
    ],
    visualRequirements: {
      stimulus: [],
      support: [],
    },
    examples: ['REK-TAFELS-BASIS', 'REK-TAFELS-UITBREIDEN'],
  },

  true_false_series: {
    type: 'true_false_series',
    category: 'interactive',
    renderer: 'src/components/practice/renderers/TrueFalseSeriesRenderer.tsx',
    description: 'Series of statements to evaluate as true or false',
    requiredFields: ['question', 'data'],
    optionalFields: ['context', 'stimulusAids', 'supportAids'],
    dataStructure: {
      required: ['statements'],
      optional: [],
    },
    validationRules: [
      'data.statements must be an array of objects with { text: string, isTrue: boolean }',
      'At least 2 statements required',
    ],
    examples: ['TAA-LEZEN-FEIT', 'TOPO-EU-LAND'],
  },

  estimation_slider: {
    type: 'estimation_slider',
    category: 'interactive',
    renderer: 'src/components/practice/renderers/EstimationSliderRenderer.tsx',
    description: 'Estimate a value on a number line',
    requiredFields: ['question', 'data'],
    optionalFields: ['context', 'stimulusAids', 'supportAids'],
    dataStructure: {
      required: ['min', 'max', 'correctValue', 'tolerancePercent'],
      optional: ['unit', 'showNumberLine'],
    },
    validationRules: [
      'data.min must be less than data.max',
      'data.correctValue must be between min and max',
      'data.tolerancePercent must be a number between 1 and 50',
    ],
    examples: ['GET-GROOT-SCHATTEN'],
  },

  categorize: {
    type: 'categorize',
    category: 'interactive',
    renderer: 'src/components/practice/renderers/CategorizeRenderer.tsx',
    description: 'Sort items into categories using tap-to-assign interaction',
    requiredFields: ['question', 'data'],
    optionalFields: ['context', 'stimulusAids', 'supportAids'],
    dataStructure: {
      required: ['categories', 'items'],
      optional: [],
    },
    validationRules: [
      'data.categories must be an array of at least 2 category name strings',
      'data.items must be an array of objects with { text: string, correctCategory: number }',
      'Each item.correctCategory must be a valid index into data.categories',
      'At least 4 items required',
    ],
    examples: ['TAA-SOORT-WW', 'TAA-SIGNAALWOORD', 'SPR-KLANK-JAGER'],
  },

  error_spotting: {
    type: 'error_spotting',
    category: 'interactive',
    renderer: 'src/components/practice/renderers/ErrorSpottingRenderer.tsx',
    description: 'Find and correct errors in a text passage',
    requiredFields: ['question', 'data'],
    optionalFields: ['context', 'stimulusAids', 'supportAids'],
    dataStructure: {
      required: ['text', 'errors'],
      optional: [],
    },
    validationRules: [
      'data.text must be a non-empty string',
      'data.errors must be an array of objects with { word: string, position: number, correction: string }',
      'Each error position must be a valid word index in the text',
      'At least 1 error required',
    ],
    examples: ['SWW-VD-HOMOFOON', 'SPR-WEET-DT', 'SWW-TT-INV-JIJ'],
  },

  grid_shade: {
    type: 'grid_shade',
    category: 'interactive',
    renderer: 'src/components/practice/renderers/GridShadeRenderer.tsx',
    description: 'Tap to shade cells in a grid to match a count or fraction',
    requiredFields: ['question', 'data'],
    optionalFields: ['context', 'stimulusAids', 'supportAids'],
    dataStructure: {
      required: ['gridRows', 'gridCols', 'targetCount', 'mode'],
      optional: ['preShaded', 'showCounter'],
    },
    validationRules: [
      'data.gridRows and data.gridCols must be positive integers',
      'data.targetCount must be a positive integer <= gridRows * gridCols',
      'data.mode must be one of: shade_count, shade_fraction, shade_to_match',
    ],
    examples: ['GET-BREUK-BASIS', 'GET-BREUK-DEEL'],
  },

  step_solver: {
    type: 'step_solver',
    category: 'interactive',
    renderer: 'src/components/practice/renderers/StepSolverRenderer.tsx',
    description: 'Solve a problem step-by-step with sequential validation',
    requiredFields: ['question', 'data'],
    optionalFields: ['context', 'stimulusAids', 'supportAids'],
    dataStructure: {
      required: ['steps'],
      optional: ['context', 'showStepsProgressively'],
    },
    validationRules: [
      'data.steps must be an array of objects with { prompt, correctAnswer }',
      'At least 1 step required',
    ],
    examples: ['REK-CONTEXT-MEERSTAPS', 'MET-OPP-SAMENGESTELD'],
  },

  sentence_parser: {
    type: 'sentence_parser',
    category: 'interactive',
    renderer: 'src/components/practice/renderers/SentenceParserRenderer.tsx',
    description: 'Assign grammatical labels to words in a sentence',
    requiredFields: ['question', 'data'],
    optionalFields: ['context', 'stimulusAids', 'supportAids'],
    dataStructure: {
      required: ['sentence', 'words', 'labels', 'correctAssignments'],
      optional: [],
    },
    validationRules: [
      'data.words must be an array of strings',
      'data.labels must be an array of strings',
      'data.correctAssignments must be a record mapping word indices to labels',
    ],
    examples: ['TAA-ONT-BASIS', 'TAA-ONT-GEZEGDE'],
  },

  comparison_judge: {
    type: 'comparison_judge',
    category: 'interactive',
    renderer: 'src/components/practice/renderers/ComparisonJudgeRenderer.tsx',
    description: 'Compare two or more items based on a criterion',
    requiredFields: ['question', 'data'],
    optionalFields: ['context', 'stimulusAids', 'supportAids'],
    dataStructure: {
      required: ['items', 'criterion', 'correctIndex'],
      optional: [],
    },
    validationRules: [
      'data.items must be an array of objects with { id, content }',
      'At least 2 items required',
      'data.correctIndex must be a valid index into data.items',
    ],
    examples: ['GET-KOMMA-VERGELIJK', 'GET-BREUK-VERGELIJK'],
  },

  thermometer_slider: {
    type: 'thermometer_slider',
    category: 'interactive',
    renderer: 'src/practice/components/renderers/ThermometerSliderRenderer.tsx',
    description:
      'Read or set a temperature on an interactive thermometer slider',
    requiredFields: ['question', 'data', 'correctAnswer'],
    optionalFields: ['context', 'stimulusAids', 'supportAids'],
    dataStructure: {
      required: ['minTemp', 'maxTemp', 'step'],
      optional: ['startTemp', 'unit', 'showScale'],
    },
    validationRules: [
      'data.minTemp must be less than data.maxTemp',
      'data.step must be a positive number',
      'correctAnswer must be a number between minTemp and maxTemp',
      'If data.unit provided, must be a string (e.g. "°C")',
    ],
    examples: ['GET-NEGATIEF-MIN', 'GET-NEGATIEF-PLUS'],
  },

  sentence_error_judge: {
    type: 'sentence_error_judge',
    category: 'interactive',
    renderer:
      'src/practice/components/renderers/SentenceErrorJudgeRenderer.tsx',
    description:
      'Pick which sentence in a set of 4 contains a spelling or grammar error',
    requiredFields: ['question', 'data'],
    optionalFields: ['context', 'stimulusAids', 'supportAids'],
    dataStructure: {
      required: ['sentences'],
      optional: [],
    },
    validationRules: [
      'data.sentences must be an array of objects with { text: string, hasError: boolean }',
      'At least 2 sentences required',
      'Exactly one sentence must have hasError: true',
      'Sentences with hasError: true must include correction (string)',
      'Optionally include explanation (string) for didactic feedback',
    ],
    examples: [
      'SWW-TT-ZIJN-HEBBEN',
      'SWW-VD-STERK',
      'SWW-VD-ZONDER-GE',
      'SWW-VT-VZFS',
    ],
  },
}

/**
 * Get question type definition
 */
export function getQuestionTypeDefinition(
  type: QuestionType,
): QuestionTypeDefinition | null {
  return QUESTION_TYPE_REGISTRY[type] || null
}

/**
 * Get all question types by category
 */
export function getQuestionTypesByCategory(
  category: 'standard' | 'interactive' | 'visual-data' | 'specialized',
): QuestionTypeDefinition[] {
  return Object.values(QUESTION_TYPE_REGISTRY).filter(
    (def) => def.category === category,
  )
}

/**
 * Get question types that require specific visual aids
 */
export function getQuestionTypesRequiringVisual(
  visualType: VisualAidType,
): QuestionTypeDefinition[] {
  return Object.values(QUESTION_TYPE_REGISTRY).filter((def) => {
    const stimulus = def.visualRequirements?.stimulus ?? []
    const support = def.visualRequirements?.support ?? []
    return stimulus.includes(visualType) || support.includes(visualType)
  })
}

/**
 * Validate question type against its definition
 */
export function validateQuestionType(
  type: QuestionType,
  question: Record<string, unknown>,
): { valid: boolean; errors: string[] } {
  const def = getQuestionTypeDefinition(type)
  if (!def) {
    return { valid: false, errors: [`Unknown question type: ${type}`] }
  }

  const errors: string[] = []

  // Check required fields
  def.requiredFields.forEach((field) => {
    if (!(field in question) || question[field] === undefined) {
      errors.push(`Missing required field: ${field}`)
    }
  })

  // Check data structure if applicable
  const data = question.data as Record<string, unknown> | undefined
  if (def.dataStructure && data && typeof data === 'object') {
    def.dataStructure.required.forEach((field) => {
      if (!(field in data) || data[field] === undefined) {
        errors.push(`Missing required data field: data.${field}`)
      }
    })
  }

  return { valid: errors.length === 0, errors }
}
